import React, { useState, useEffect, useCallback } from 'react';
import DataService from '../../service/DataService';
import { estimationRuleOf, listLocalOrders, type EstimationRule, type OrderMeta } from '../../service/OrderEta';
import OrderEtaLine from '../OrderEta/OrderEtaLine';
import { X } from 'lucide-react';
import { useTheme } from '../../hooks/ThemeContext';
import { Navigate, Link } from 'react-router-dom';
import { useDineCentralAuth } from '../../hooks/useDineCentralAuth';
import Spinner from '../../components/Spinner/Spinner';
import './OrderHistoryPageStyles.css';
import { getRemoteEnv } from '../../config/remoteEnv';
import { DINECENTRAL_AUTH } from '../../Firebase.dinecentral';

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

const ORDERS_WORKER_URL =
  process.env.REACT_APP_ORDERS_WORKER_URL ||
  'https://dineplatform-orders.jotlumanagement.workers.dev';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OrderCharge {
  name: string;
  alias?: string;
  amount: number;
}

interface CartItem {
  id?: string;
  productId?: string;
  productName?: string;
  name?: string;
  quantity: number;
  selectedChoices?: { name: string; value: string; parent?: string | null }[];
  additionalOptionIds?: string[];
  additionalOptions?: { id: string; name: string; price: number }[];
  additionalRequests?: string;
}

interface BreakdownItem {
  name: string;
  quantity: number;
  price: number;
  additionalOptions: { id: string; name: string; price: number }[];
  selectedChoices: { name: string; value: string; parent: string | null }[];
  additionalRequests: string;
}

interface Breakdown {
  metadata: {
    orderCode?: string;
    orderType?: string;
    orderTime?: string;
    storeId?: string;
    restaurantName?: string;
  };
  items: BreakdownItem[];
  totals: {
    subtotal: number;
    additionalCharges: OrderCharge[];
    total: number;
  };
  payment?: {
    method?: string;
    last_four?: string | null;
    auth_code?: string | null;
  };
}

interface Order {
  order_id: string;
  app_id: string;
  store_id: string;
  order_code: string;
  order_type: string;
  payment_method: string;
  status: string;
  payment_status: string;
  total: number;
  customer_name: string;
  order_time: string;
  charges: OrderCharge[];
  orderData?: {
    cart?: CartItem[];
    finalCart?: CartItem[];
    restaurantName?: string;
    orderType?: string;
    additionalCharges?: OrderCharge[];
    breakdown?: Breakdown;
    /** Delivery quote taken at checkout (driving minutes / distance). */
    deliveryQuote?: { estimatedDuration?: number | null; drivingDistance?: number | null; deliveryType?: string | null } | null;
    /** Live courier status (Uber Direct) — carries a dropoff ETA once assigned. */
    deliveryStatus?: { dropoffEta?: number | string | null; status?: string | null } | null;
    /** Kitchen-set ready time from the KDS. */
    readyAt?: string | null;
  };
}

interface OrdersResponse {
  orders: Order[];
  nextCursor: string | null;
  hasMore: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function fetchOrders(
  appId: string,
  cursor?: string,
  testMode: boolean = false
): Promise<OrdersResponse> {
  const user = DINECENTRAL_AUTH.currentUser;
  if (!user) throw new Error('Not authenticated');

  const token = await user.getIdToken();
  const params = new URLSearchParams({ appId });
  if (cursor) params.set('cursor', cursor);
  params.set('test', testMode ? 'true' : 'false');

  const response = await fetch(
    `${ORDERS_WORKER_URL}/orders/my-orders?${params.toString()}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (response.status === 401 || response.status === 403) throw new Error('AUTH_ERROR');
  if (!response.ok) throw new Error(`Failed to fetch orders: ${response.status}`);

  return response.json();
}

function formatDate(dateString: string): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatTime(dateString: string): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function getStatusClassName(status: string): string {
  switch (status?.toUpperCase()) {
    case 'COMPLETED':  return 'history-status-completed';
    case 'CANCELLED':  return 'history-status-cancelled';
    case 'COOKING':    return 'history-status-cooking';
    case 'ON_ITS_WAY': return 'history-status-on-way';
    default:           return 'history-status-default';
  }
}

function getBreakdown(order: Order): Breakdown | null {
  return order.orderData?.breakdown ?? null;
}

function getCartFallback(order: Order): CartItem[] {
  const data = order.orderData;
  if (!data) return [];
  return data.finalCart || data.cart || [];
}

function getItemNameFallback(item: CartItem): string {
  return item.productName || item.name || item.productId || item.id || 'Item';
}

// ---------------------------------------------------------------------------
// ItemList — prefers breakdown, falls back to raw cart
// ---------------------------------------------------------------------------

interface ItemListProps {
  order: Order;
  theme: any;
  currencySymbol: string;
}

const ItemList: React.FC<ItemListProps> = ({ order, theme, currencySymbol }) => {
  const breakdown = getBreakdown(order);

  if (breakdown && breakdown.items.length > 0) {
    return (
      <>
        {breakdown.items.map((item, index) => (
          <div
            key={index}
            className="history-order-item"
            style={{ backgroundColor: theme.backgroundColor }}
          >
            <div className="history-item-main">
              <span className="history-item-quantity">{item.quantity}&nbsp;×</span>
              <div className="history-item-details">
                <div className="history-item-name-row">
                  <span className="history-item-name">{item.name}</span>
                  {item.price > 0 && (
                    <span className="history-item-price">
                      {currencySymbol}{item.price.toFixed(2)}
                    </span>
                  )}
                </div>

                {item.selectedChoices?.map((choice, i) => (
                  <div key={i} className="history-item-choice">
                    <span>{choice.name}: {choice.value}</span>
                  </div>
                ))}

                {item.additionalOptions?.map((opt, i) => (
                  <div key={i} className="history-item-option">
                    + {opt.name}
                    {opt.price > 0 && (
                      <span className="history-item-option-price">
                        {' '}{currencySymbol}{opt.price.toFixed(2)}
                      </span>
                    )}
                  </div>
                ))}

                {item.additionalRequests && (
                  <div className="history-item-note">Note: {item.additionalRequests}</div>
                )}
              </div>
            </div>
          </div>
        ))}
      </>
    );
  }

  // Fallback — no breakdown available
  const cart = getCartFallback(order);
  return (
    <>
      {cart.map((item, index) => (
        <div
          key={index}
          className="history-order-item"
          style={{ backgroundColor: theme.backgroundColor }}
        >
          <div className="history-item-main">
            <span className="history-item-quantity">{item.quantity}&nbsp;×</span>
            <div className="history-item-details">
              <div className="history-item-name">{getItemNameFallback(item)}</div>

              {item.selectedChoices?.map((choice, i) => (
                <div key={i} className="history-item-choice">
                  <span>{choice.name}: {choice.value}</span>
                </div>
              ))}

              {item.additionalOptions && item.additionalOptions.length > 0 && (
                <div className="history-item-options">
                  {item.additionalOptions.map((opt, i) => (
                    <div key={i} className="history-item-option">+ {opt.name}</div>
                  ))}
                </div>
              )}

              {!item.additionalOptions?.length && item.additionalOptionIds && item.additionalOptionIds.length > 0 && (
                <div className="history-item-options">
                  {item.additionalOptionIds.map((_, i) => (
                    <div key={i} className="history-item-option">+ Add-on</div>
                  ))}
                </div>
              )}

              {item.additionalRequests && (
                <div className="history-item-note">Note: {item.additionalRequests}</div>
              )}
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

// ---------------------------------------------------------------------------
// Charges — prefers breakdown totals, falls back to raw charges
// ---------------------------------------------------------------------------

interface ChargesProps {
  order: Order;
  theme: any;
  currencySymbol: string;
}

const Charges: React.FC<ChargesProps> = ({ order, theme, currencySymbol }) => {
  const breakdown = getBreakdown(order);

  if (breakdown) {
    const allCharges: OrderCharge[] = [
      { name: 'Subtotal', amount: breakdown.totals.subtotal },
      ...breakdown.totals.additionalCharges,
    ];
    return (
      <div className="history-charges-section" style={{ borderColor: theme.borderColor }}>
        {allCharges.map((charge, i) => (
          <div
            key={i}
            className="history-charge-row"
            style={{ color: theme.textColor, opacity: charge.name === 'Subtotal' ? 0.7 : 1 }}
          >
            <span>{charge.alias || charge.name}</span>
            <span>{currencySymbol}{Number(charge.amount).toFixed(2)}</span>
          </div>
        ))}
      </div>
    );
  }

  if (order.charges && order.charges.length > 0) {
    return (
      <div className="history-charges-section" style={{ borderColor: theme.borderColor }}>
        {order.charges.map((charge, i) => (
          <div
            key={i}
            className="history-charge-row"
            style={{ color: theme.textColor, opacity: charge.name === 'Subtotal' ? 0.7 : 1 }}
          >
            <span>{charge.name}</span>
            <span>{currencySymbol}{Number(charge.amount).toFixed(2)}</span>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface OrderHistoryPageProps {
  appId?: string;
  environment?: 'live' | 'test';
}

const OrderHistoryPage: React.FC<OrderHistoryPageProps> = ({ appId, environment = 'live' }) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { theme } = useTheme();
  const auth = useDineCentralAuth();
  const resolvedAppId = appId || getRemoteEnv().appId;
  const isTest = environment === 'test';

  // ── Estimated ready / arrival time ────────────────────────────────────────
  // The estimation rule lives on each store's config; cache per store so a
  // multi-store history only fetches each once.
  const [rules, setRules] = useState<Record<string, EstimationRule | null>>({});
  useEffect(() => {
    const localIds = auth.user ? [] : listLocalOrders().map(m => m.storeId).filter((x): x is string => !!x);
    const missing = Array.from(new Set([...orders.map(o => o.store_id), ...localIds].filter(Boolean))).filter(id => !(id in rules));
    if (missing.length === 0) return;
    missing.forEach(id => {
      setRules(prev => ({ ...prev, [id]: null }));
      DataService.GetData('storeConfig', id).then((cfg: any) => {
        setRules(prev => ({ ...prev, [id]: estimationRuleOf(cfg) }));
      }).catch(() => { /* no rule → no estimate */ });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders]);
  // Store names + addresses from the public directory (the order record
  // itself only carries the store id). One fetch per app.
  const [stores, setStores] = useState<Record<string, { name?: string; address?: string; city?: string; zipCode?: string }>>({});
  useEffect(() => {
    if (!resolvedAppId) return;
    fetch(`https://www.dineplatform.com/r/${encodeURIComponent(resolvedAppId)}/restaurants.json`)
      .then(r => (r.ok ? r.json() : null))
      .then((all: any) => { if (all && typeof all === 'object') setStores(all); })
      .catch(() => { /* directory unreachable → ids stay */ });
  }, [resolvedAppId]);
  const storeNameOf = (o: Order) => o.orderData?.breakdown?.metadata?.restaurantName || o.orderData?.restaurantName || stores[o.store_id]?.name || o.store_id;
  const storeAddressOf = (o: Order) => {
    const st = stores[o.store_id]; if (!st) return '';
    return [st.address, st.city, st.zipCode].filter(Boolean).join(', ');
  };
  const orderTypeOf = (o: Order) => String(o.orderData?.breakdown?.metadata?.orderType || o.order_type || o.orderData?.orderType || '').toLowerCase();
  const etaInputFor = (o: Order) => ({
    readyAt: o.orderData?.readyAt || null,
    orderTime: o.order_time,
    orderType: o.orderData?.breakdown?.metadata?.orderType || o.order_type || o.orderData?.orderType,
    status: o.status,
    deliveryQuote: o.orderData?.deliveryQuote || null,
    deliveryStatus: o.orderData?.deliveryStatus || null,
    rule: rules[o.store_id] || null,
  });

  // ── Deep link from checkout: /recent?order=<code or id> ──────────────────
  // The order is mirrored to the history store a moment after payment, so
  // poll briefly until it shows up, then open it.
  const [wantedOrder, setWantedOrder] = useState<string | null>(() => new URLSearchParams(window.location.search).get('order'));
  const wantedTries = React.useRef(0);
  // An order is "partial" while only the payment-time write exists: the pool
  // fills in items, type and time a few seconds later.
  const isPartial = (o: Order) => !(o.orderData?.breakdown?.items?.length || o.orderData?.finalCart?.length || o.orderData?.cart?.length);
  useEffect(() => {
    if (!wantedOrder) return;
    const hit = orders.find(o => o.order_code === wantedOrder || o.order_id === wantedOrder);
    if (hit) {
      setSelectedOrder(hit);
      const sp = new URLSearchParams(window.location.search);
      if (sp.has('order')) { sp.delete('order'); window.history.replaceState({}, document.title, `${window.location.pathname}${sp.toString() ? `?${sp}` : ''}`); }
      if (!isPartial(hit)) { setWantedOrder(null); return; } // complete — stop polling
    }
    if (loading || wantedTries.current >= 20) return; // ~60 s
    const t = setTimeout(() => { wantedTries.current += 1; void loadOrders(); }, 3000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, loading, wantedOrder]);
  // Whatever is open follows the latest fetched copy (status changes, the
  // pool's completion of a partial order, a kitchen-set ready time).
  useEffect(() => {
    if (!selectedOrder) return;
    const fresh = orders.find(o => o.order_id === selectedOrder.order_id);
    if (fresh && fresh !== selectedOrder) setSelectedOrder(fresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders]);
  // While an active order is open, refresh every 30 s so the kitchen's
  // ready-time changes and status moves show without a reload.
  useEffect(() => {
    if (!selectedOrder) return;
    const done = ['COMPLETED', 'CANCELLED', 'AUTO_CLOSED'].includes(String(selectedOrder.status || '').toUpperCase());
    if (done) return;
    const t = setInterval(() => { void loadOrders(); }, 30_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrder?.order_id, selectedOrder?.status]);

  useEffect(() => {
    if (selectedOrder) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [selectedOrder]);

  const loadOrders = useCallback(async (nextCursor?: string) => {
    if (!resolvedAppId) return;
    try {
      const result = await fetchOrders(resolvedAppId, nextCursor, isTest);
      setOrders(prev => (nextCursor ? [...prev, ...result.orders] : result.orders));
      setCursor(result.nextCursor);
      setHasMore(result.hasMore);
      setError(null);
    } catch (err: any) {
      if (err.message === 'AUTH_ERROR') {
        try {
          await DINECENTRAL_AUTH.currentUser?.getIdToken(true);
          const retryResult = await fetchOrders(resolvedAppId, nextCursor, isTest);
          setOrders(prev => (nextCursor ? [...prev, ...retryResult.orders] : retryResult.orders));
          setCursor(retryResult.nextCursor);
          setHasMore(retryResult.hasMore);
          setError(null);
        } catch {
          setError('Unable to load orders. Please try again.');
        }
      } else {
        console.error('Error loading orders:', err);
        setError('Unable to load orders. Please try again.');
      }
    }
  }, [resolvedAppId, isTest]);

  useEffect(() => {
    if (!auth.user) return;
    setLoading(true);
    loadOrders().finally(() => setLoading(false));
  }, [auth.user, loadOrders]);

  const handleLoadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    await loadOrders(cursor);
    setLoadingMore(false);
  };

  if (auth.loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
        <Spinner />
      </div>
    );
  }

  if (!auth.user) {
    // Guests: the orders this browser placed, with their estimates — and a
    // sign-in link that comes back HERE (router path, not the full browser
    // path: on /r/{app} hosts the latter already carries the brand prefix and
    // the login's navigate() would double it into a blank page).
    const local = listLocalOrders();
    const backTo = encodeURIComponent(`/recent${window.location.search}`);
    return (
      <div className="history-container" style={{ backgroundColor: theme.backgroundColor }}>
        <h1 className="history-title" style={{ color: theme.textColor }}>Order History</h1>
        <p className="history-sub" style={{ color: theme.textColor }}>
          Orders placed on this device. <Link to={`/login?redirect=${backTo}`} style={{ color: theme.textColor, fontWeight: 700 }}>Sign in</Link> to see live status and orders from other devices.
        </p>
        {local.length === 0 && (
          <div className="history-empty-state" style={{ color: theme.textColor }}>
            <div className="history-empty-message">No orders on this device yet.</div>
          </div>
        )}
        <div className="history-orders-grid">
          {local.map((m: OrderMeta) => (
            <div key={m.orderCode} className="history-order-card" style={{ backgroundColor: theme.cardColor, borderColor: theme.borderColor, color: theme.textColor, cursor: 'default' }}>
              <div className="history-restaurant-name">{(m.storeId && stores[m.storeId]?.name) || m.storeId || ''}</div>
              <div className="history-order-info">
                <span className="history-order-code">#{m.orderCode}</span>
                <span className="history-order-date">{formatDate(m.orderTime)} · {formatTime(m.orderTime)}</span>
              </div>
              <OrderEtaLine input={{ orderTime: m.orderTime, orderType: m.orderType, deliveryQuote: m.deliveryQuote, rule: (m.storeId && rules[m.storeId]) || null }} color={theme.textColor} compact />
              <div className="history-order-bottom">
                <span className="history-order-type">{String(m.orderType || 'order').toLowerCase()}{m.itemCount ? ` · ${m.itemCount} item${m.itemCount === 1 ? '' : 's'}` : ''}</span>
                <span className="history-total">{m.total != null ? `${getRemoteEnv().currencySymbol || ''}${Number(m.total).toFixed(2)}` : ''}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="history-container" style={{ backgroundColor: theme.backgroundColor }}>
        <h1 className="history-title" style={{ color: theme.textColor }}>Order History</h1>
        <p className="history-sub" style={{ color: theme.textColor }}>Everything you've ordered from us, newest first.</p>
        <div className="history-orders-grid">
          {[0, 1, 2].map((i) => <div key={i} className="history-skel" />)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="history-container" style={{ backgroundColor: theme.backgroundColor }}>
        <h1 className="history-title" style={{ color: theme.textColor }}>Order History</h1>
        <div className="history-empty-state" style={{ color: theme.textColor }}>
          <div className="history-empty-message">{error}</div>
          <button
            onClick={() => { setLoading(true); loadOrders().finally(() => setLoading(false)); }}
            style={{
              marginTop: '16px', padding: '10px 24px', borderRadius: '8px',
              cursor: 'pointer', border: 'none',
              backgroundColor: theme.cardColor, color: theme.textColor,
              fontSize: '15px', fontWeight: 500,
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="history-container" style={{ backgroundColor: theme.backgroundColor }}>
        <h1 className="history-title" style={{ color: theme.textColor }}>Order History</h1>
        <p className="history-sub" style={{ color: theme.textColor }}>Everything you've ordered from us, newest first.</p>
        <div className="history-empty-state" style={{ color: theme.textColor }}>
          <div className="history-empty-message">Nothing here yet</div>
          <div className="history-empty-subtitle">Your orders will appear here as soon as you place one.</div>
        </div>
      </div>
    );
  }

  const currencySymbol = getRemoteEnv().currencySymbol;

  return (
    <div className="history-container" style={{ backgroundColor: theme.backgroundColor }}>
      <h1 className="history-title" style={{ color: theme.textColor }}>Order History</h1>
      <p className="history-sub" style={{ color: theme.textColor }}>Everything you've ordered from us, newest first.</p>

      <div className="history-orders-grid">
        {orders.map(order => (
          <div
            key={order.order_id}
            className="history-order-card"
            onClick={() => setSelectedOrder(order)}
            style={{ backgroundColor: theme.cardColor, borderColor: theme.borderColor, color: theme.textColor }}
          >
            <div className="history-restaurant-name">{storeNameOf(order)}</div>
            <div className="history-order-info">
              <span className="history-order-code">#{order.order_code}</span>
              <span className="history-order-date">{formatDate(order.order_time)}</span>
            </div>
            <OrderEtaLine input={etaInputFor(order)} color={theme.textColor} compact />
            <div className="history-order-bottom">
              <span className={`history-status-badge ${getStatusClassName(order.status)}`}>
                {order.status?.replace(/_/g, ' ')}
              </span>
              <span className="history-total">
                {order.total == null ? "Processing…" : `${currencySymbol}${Number(order.total).toFixed(2)}`}
              </span>
            </div>
          </div>
        ))}
      </div>

      {hasMore && (
        <div className="history-load-more-btn">
          <button
            onClick={handleLoadMore}
            disabled={loadingMore}
            style={{
              padding: '11px 28px',
              borderRadius: '10px',
              border: `1px solid ${theme.borderColor}`,
              backgroundColor: theme.cardColor,
              color: theme.textColor,
              cursor: loadingMore ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              opacity: loadingMore ? 0.6 : 1,
            }}
          >
            {loadingMore ? 'Loading…' : 'Load more orders'}
          </button>
        </div>
      )}

      {selectedOrder && (
        <div className="history-modal-overlay" onClick={() => setSelectedOrder(null)}>
          <div
            className="history-modal-content"
            onClick={e => e.stopPropagation()}
            style={{ backgroundColor: theme.cardColor, color: theme.textColor }}
          >
            <div className="history-modal-header" style={{ borderColor: theme.borderColor }}>
              <button
                className="history-close-button"
                onClick={() => setSelectedOrder(null)}
                style={{ color: theme.textColor }}
                aria-label="Close"
              >
                <X size={20} />
              </button>

              <div className="history-modal-restaurant">
                {storeNameOf(selectedOrder)}
              </div>
              {(orderTypeOf(selectedOrder) === 'pickup' || orderTypeOf(selectedOrder) === 'dinein') && storeAddressOf(selectedOrder) && (
                <div style={{ fontSize: 12, opacity: 0.7, marginTop: -6, marginBottom: 8 }}>
                  {orderTypeOf(selectedOrder) === 'pickup' ? 'Collect from ' : ''}{storeAddressOf(selectedOrder)}
                </div>
              )}

              <div className="history-modal-info">
                <div className="history-order-header">
                  <span className="history-order-code">Order #{selectedOrder.order_code}</span>
                  <div className="history-timestamp">
                    {formatDate(selectedOrder.order_time)} · {formatTime(selectedOrder.order_time)}
                  </div>
                </div>
                <div className="history-order-header">
                  <span className={`history-status-badge ${getStatusClassName(selectedOrder.status)}`}>
                    {selectedOrder.status?.replace(/_/g, ' ')}
                  </span>
                  <span className="history-order-type">
                    {(
                      selectedOrder.orderData?.breakdown?.metadata?.orderType
                      || selectedOrder.order_type
                      || selectedOrder.orderData?.orderType
                      || 'order'
                    ).toLowerCase()}
                  </span>
                </div>
                <OrderEtaLine input={etaInputFor(selectedOrder)} color={theme.textColor} />
              </div>
            </div>

            <div className="history-scrollable-content">
              {isPartial(selectedOrder) ? (
                <div style={{ padding: '18px 0', textAlign: 'center', opacity: 0.75, fontSize: 14 }}>
                  Your order went through — the details will appear here in a few seconds…
                </div>
              ) : (
                <ItemList order={selectedOrder} theme={theme} currencySymbol={currencySymbol} />
              )}
            </div>

            <Charges order={selectedOrder} theme={theme} currencySymbol={currencySymbol} />

            <div className="history-modal-footer" style={{ borderColor: theme.borderColor }}>
              <span className="history-total-label">Total Paid</span>
              <span className="history-total-amount">
                {selectedOrder.total == null ? "Processing…" : `${currencySymbol}${Number(selectedOrder.total).toFixed(2)}`}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderHistoryPage;